/**
 * Canonical set of known admin permission keys used for:
 * - typed route metadata (`AdminPermissionKey`)
 * - navigation gating in `apps/admin/src/routing/adminRoutes.tsx`
 * - Admin E2E bypass allowlists (local/test flows)
 *
 * @remarks
 * This list is intentionally explicit (not "any string") so missing keys are caught at
 * compile-time and during tests, rather than silently creating unguarded routes.
 *
 * Keep this in sync with the backend RBAC permission universe for Admin V2.
 *
 * @see services/api/src/service/adminV2/rbacService.ts
 * @see apps/admin/src/routing/adminRoutes.tsx
 */
export const ADMIN_PERMISSION_KEYS = [
  "admin.v2.access",
  "organisations.access_all",
  "rbac.manage",
  "approvals.view",
  "approvals.approve",
  "audit.view",
  "crm.view",
  "crm.edit",
  "crm.view_pii",
  "support.view",
  "support.manage",
  "support.spoof_user",
  "kb.manage",
  "forms.manage",
  "audiences.view",
  "tags.manage",
  "segments.manage",
  "messaging.view",
  "messaging.manage_identities",
  "messaging.manage_templates",
  "messaging.send_broadcast",
  "messaging.manage_sequences",
  "automation.view",
  "automation.edit",
  "automation.publish",
  "billing.view",
  "billing.manage_offers",
  "billing.manage_coupons",
  "billing.manage_affiliates",
  "billing.refund",
  "billing.override",
  "exports.run",
  "exports.run_sensitive",
  "analytics.view",
  "analytics.view_sensitive",
  "learning.view",
  "learning.manage",
  "learning.manage_scores",
  "featureflags.manage",
  "entitlements.manage",
  "integrations.view",
  "integrations.manage",
  "jobs.view",
  "search.view",
] as const;

export type AdminPermissionKey = (typeof ADMIN_PERMISSION_KEYS)[number];
